import { ReactNode, forwardRef } from 'react';

import {
  Chip,
  TableCell,
  TableCellProps,
  Tooltip,
  makeStyles,
} from '@material-ui/core';

import { Link } from '@backstage/core-components';

import { Flex } from '@spotify-internal/encore-web';
import {
  DemoTag,
  type DemoTagProps,
  MetadataText,
} from '@spotify/confidence-core-react';

import {
  type WorkflowInstanceComments,
  useAssignCount,
} from '../../hooks/useAssignCount';

import styles from './NameCell.module.css';

export const IconPlaceholder = () => (
  <span className={styles.iconPlaceholder} />
);

interface NameCellProps extends TableCellProps {
  href: string;
  comments?: WorkflowInstanceComments | null;
  icon?: ReactNode;
  kind: string;
  secondaryText?: ReactNode;
  displayName: string;
  labels?: DemoTagProps['labels'];
}

const useStyles = makeStyles({
  assignCountBadge: {
    verticalAlign: 'baseline',
    marginLeft: 4,
  },
});

export const NameCell = forwardRef<HTMLTableCellElement, NameCellProps>(
  (
    {
      href,
      displayName,
      icon,
      kind,
      comments,
      secondaryText,
      labels,
      ...props
    },
    ref,
  ) => {
    const classes = useStyles();

    const assignCount = useAssignCount(comments?.comments ?? []);

    const content = (
      <>
        <Link to={href}>{displayName}</Link>
        {labels && <DemoTag labels={labels} />}
        {assignCount > 0 && (
          <Tooltip title={`${assignCount} assigned threads`}>
            <Chip
              size="small"
              label={assignCount.toString()}
              className={classes.assignCountBadge}
            />
          </Tooltip>
        )}
        <MetadataText items={[kind, secondaryText]} />
      </>
    );

    return (
      <TableCell {...props} ref={ref}>
        {icon ? (
          <Flex alignItems="center" gap="tighter">
            <span className={styles.iconWrapper}>{icon}</span>
            <div className={styles.textContent}>{content}</div>
          </Flex>
        ) : (
          content
        )}
      </TableCell>
    );
  },
);
NameCell.displayName = 'NameCell';
